/**
 * Record sheets are addressed by the `open` search param: a stack of
 * `type:id` pairs joined by commas, at most four deep. `stab` is the top
 * sheet's tab and `sw` its width. A shared link reopens the exact stack over
 * the same list.
 */
import { z } from 'zod';

const SHEET_TYPES = [
  'cust',
  'tx',
  'issue',
  'case',
  'change',
  'kyc',
  'staff',
  'money',
  'referral',
  'audit',
  'fee',
  'limit',
] as const;

export type SheetType = (typeof SHEET_TYPES)[number];

export interface SheetRef {
  type: SheetType;
  id: string;
}

export const MAX_SHEET_DEPTH = 4;

function isSheetType(value: string): value is SheetType {
  return (SHEET_TYPES as readonly string[]).includes(value);
}

/** "tx:9f2a…" */
export function sheetKey(ref: SheetRef): string {
  return `${ref.type}:${ref.id}`;
}

/** Parses one "type:id" pair. Ids may contain anything but a comma. */
function parseSheetRef(value: string): SheetRef | null {
  const at = value.indexOf(':');
  if (at <= 0) return null;
  const type = value.slice(0, at);
  const id = value.slice(at + 1).trim();
  if (!isSheetType(type) || id.length === 0) return null;
  return { type, id };
}

/** Parses the `open` param. Invalid pairs are dropped; the stack keeps the last four. */
export function parseSheetStack(param: string | undefined | null): SheetRef[] {
  if (!param) return [];
  const refs = param
    .split(',')
    .map((part) => parseSheetRef(part.trim()))
    .filter((ref): ref is SheetRef => ref !== null);
  return refs.slice(-MAX_SHEET_DEPTH);
}

/** Serialises a stack for the `open` param, or undefined to remove it. */
export function serializeSheetStack(
  refs: readonly SheetRef[],
): string | undefined {
  if (refs.length === 0) return undefined;
  return refs.slice(-MAX_SHEET_DEPTH).map(sheetKey).join(',');
}

const sheetWidthSchema = z.enum(['standard', 'wide']);
export type SheetWidth = z.infer<typeof sheetWidthSchema>;

/**
 * The search params every signed-in page shares. Invalid values are dropped
 * so a hand-edited link still opens the page.
 */
export const sheetSearchSchema = z.object({
  open: z.string().min(1).optional().catch(undefined),
  stab: z.string().min(1).optional().catch(undefined),
  sw: sheetWidthSchema.optional().catch(undefined),
});
